import { useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_SCOPE, TChatScope } from '../constants';

interface TScopeSelection {
    leadId: string | null;
    scope: TChatScope;
}

/**
 * Which chat is active: the lead chat (default when a lead is available) or the
 * general chat. `scopeKeyRef` always holds the current scope so async responses
 * can tell whether the user switched chats meanwhile.
 */
export const useAssistantScope = (availableLeadId: string | null) => {
    const [selection, setSelection] = useState<TScopeSelection>({
        leadId: null,
        scope: CHAT_SCOPE.GENERAL,
    });
    const activeScope: TChatScope = availableLeadId
        ? selection.leadId === availableLeadId
            ? selection.scope
            : CHAT_SCOPE.LEAD
        : CHAT_SCOPE.GENERAL;
    const contextLeadId =
        activeScope === CHAT_SCOPE.LEAD ? availableLeadId : null;
    const scopeKey = contextLeadId || CHAT_SCOPE.GENERAL;
    const scopeKeyRef = useRef(scopeKey);

    useEffect(() => {
        scopeKeyRef.current = scopeKey;
    }, [scopeKey]);

    useEffect(() => {
        setSelection({
            leadId: availableLeadId,
            scope: availableLeadId ? CHAT_SCOPE.LEAD : CHAT_SCOPE.GENERAL,
        });
    }, [availableLeadId]);

    const setChatScope = useCallback(
        (scope: TChatScope) => {
            if (scope === CHAT_SCOPE.LEAD && !availableLeadId) return;
            setSelection({ leadId: availableLeadId, scope });
        },
        [availableLeadId]
    );

    return { activeScope, contextLeadId, scopeKey, scopeKeyRef, setChatScope };
};
